"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  ArrowRight,
  MessageCircle,
  X,
  Send,
  Bot,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import { OPEN_CHATBOT_EVENT } from "@/lib/chatbot-events";

// ─── Session storage key ───────────────────────────────────────────────────────
const PRIVACY_SESSION_KEY = "gp_chat_privacy_accepted";
const CHATBOT_WEBHOOK_URL =
  "https://gruppopiccirillo.app.n8n.cloud/webhook/7f4a8515-7a04-4298-a536-a4853259434e/chat";
const ORDER_STATUS_RESPONSE = `**Controlla lo stato del tuo ordine**
Accedi all'[Area Clienti](https://gruppopiccirillo.it/auth/accedi) per verificare lo stato di avanzamento del tuo ordine.
Non hai le credenziali di accesso? Contattaci al [06 21116020](tel:+390621116020) e un nostro consulente ti fornirà assistenza.

**Richiedi assistenza post-vendita**
Per qualsiasi esigenza dopo la consegna del veicolo, il nostro team è a tua disposizione al [06 21116020](tel:+390621116020).`;
const QUICK_REPLIES = [
  {
    id: "vehicle_search",
    label: "Acquista il veicolo su misura per te",
    action: "vehicle_search",
  },
  {
    id: "rent_to_buy",
    label: "Il Rent to Buy su misura per te",
    action: "navigate",
    href: "https://gruppopiccirillo.it/rent-to-buy",
  },
  {
    id: "sell_your_car",
    label: "Vendi la tua auto",
    action: "navigate",
    href: "https://gruppopiccirillo.it/vendi-la-tua-auto/permuta",
  },
  {
    id: "order_status",
    label: "Hai già acquistato la tua auto?",
    action: "local_response",
    response: ORDER_STATUS_RESPONSE,
  },
] as const;
const PERMUTA_PROMPTS = [
  {
    id: "permuta_payment",
    label: "Come viene pagata la mia auto?",
    message: "Se vi vendo la mia auto, come vengo pagato?",
  },
  {
    id: "permuta_price_change",
    label: "Il prezzo pu\u00f2 cambiare?",
    message: "La valutazione che mi fate della mia auto pu\u00f2 cambiare?",
  },
  {
    id: "permuta_scratches",
    label: "La mia auto ha qualche graffio, influisce sulla valutazione?",
    message: "Nella valutazione della mia auto, se ha qualche graffio, influisce?",
  },
  {
    id: "permuta_dropoff",
    label: "Devo portare l'auto in sede?",
    message: "Se voglio fare una valutazione della mia auto, devo portarvela in sede?",
  },
] as const;
const VEHICLE_SEARCH_HASH = "#cerca-veicolo";
const CHATBOT_QUICK_REPLY_EVENT = "chatbot_quick_reply_click";
const CHATBOT_ANALYTICS_URL =
  process.env.NEXT_PUBLIC_CHATBOT_ANALYTICS_URL ??
  "https://staging-api.gruppopiccirillo.it/dashedil/api/analytics/vehicle-search-click";

type AnalyticsWindow = Window & {
  dataLayer?: Array<Record<string, unknown>>;
};

// ─── Types ─────────────────────────────────────────────────────────────────────
type Message = {
  id: string;
  role: "user" | "bot";
  text: string;
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const formatBoldText = (text: string, keyPrefix: string) => {
  const boldRegex = /(\*\*[\s\S]+?\*\*)/g;
  const parts = text.split(boldRegex);

  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <strong key={`${keyPrefix}-bold-${i}`} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }

    return (
      <span key={`${keyPrefix}-text-${i}`} className="whitespace-pre-wrap">
        {part}
      </span>
    );
  });
};
const formatMessageText = (text: string, isUser: boolean) => {
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|tel:[^\s)]+)\)|(https?:\/\/[^\s]+)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const [i, match] of Array.from(text.matchAll(linkRegex)).entries()) {
    if (match.index > lastIndex) {
      parts.push(
        ...formatBoldText(text.slice(lastIndex, match.index), `part-${i}`)
      );
    }

    const linkLabel = match[1];
    let href = match[2] ?? match[3];
    const isNamedLink = Boolean(match[2]);
    let trailing = "";

    if (!isNamedLink && /[.,;?!)\]]$/.test(href)) {
      trailing = href.slice(-1);
      href = href.slice(0, -1);
    }

    const isWebLink = href.startsWith("http");
    parts.push(
      <React.Fragment key={`link-${i}`}>
        <a
          href={href}
          target={isWebLink ? "_blank" : undefined}
          rel={isWebLink ? "noopener noreferrer" : undefined}
          className={cn(
            "underline hover:opacity-80 transition-opacity font-medium",
            !isNamedLink && "break-all",
            isUser ? "text-white" : "text-primary"
          )}
        >
          {linkLabel ?? href}
        </a>
        {trailing && <span>{trailing}</span>}
      </React.Fragment>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(...formatBoldText(text.slice(lastIndex), "part-last"));
  }

  return parts;
};

// ─── Privacy Consent Banner ────────────────────────────────────────────────────
interface PrivacyConsentProps {
  onAccept: () => void;
}

function PrivacyConsent({ onAccept }: PrivacyConsentProps) {
  const [isExiting, setIsExiting] = useState(false);

  const handleAccept = () => {
    setIsExiting(true);
    // Aspetta la fine del fade-out (300ms) prima di aggiornare lo stato padre
    setTimeout(onAccept, 320);
  };

  return (
    <div
      className={cn(
        "px-5 py-5 bg-white border-t border-zinc-100 transition-all duration-300 ease-in-out",
        isExiting ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
      )}
      style={{
        transitionProperty: "opacity, transform",
      }}
    >
      {/* Icona decorativa */}
      <div className="flex items-center gap-2.5 mb-3.5">
        <div
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #000D7D, #001AFF)" }}
        >
          <ShieldCheck className="w-3.5 h-3.5 text-white" />
        </div>
        <span
          className="text-[10px] tracking-[0.12em] font-semibold uppercase"
          style={{ color: "#000D7D" }}
        >
          Privacy &amp; GDPR
        </span>
      </div>

      {/* Testo */}
      <p className="text-[12.5px] leading-[1.65] text-zinc-500 font-light mb-4">
        Per iniziare la conversazione, ti invitiamo a prendere visione e
        accettare la nostra{" "}
        <a
          href="https://www.iubenda.com/privacy-policy/31103949"
          target="_blank"
          rel="noopener noreferrer"
          className="text-zinc-700 underline underline-offset-2 decoration-zinc-300 hover:text-primary hover:decoration-primary transition-colors font-normal"
        >
          Privacy Policy
        </a>{" "}
        in conformità al{" "}
        <span className="text-zinc-600 font-normal">GDPR</span>.
      </p>

      {/* Bottone CTA */}
      <button
        onClick={handleAccept}
        className="w-full py-2.5 px-4 rounded-xl text-[13px] font-medium tracking-wide text-white transition-all duration-200 hover:opacity-90 hover:shadow-lg active:scale-[0.98]"
        style={{
          background: "linear-gradient(to right, #000D7D, #001AFF)",
          boxShadow: "0 4px 14px rgba(0, 13, 125, 0.25)",
        }}
      >
        Accetta e Continua
      </button>
    </div>
  );
}

// ─── Main Chatbot Component ────────────────────────────────────────────────────
export default function Chatbot() {
  const pathname = usePathname();
  const isPermutaArea = pathname.startsWith("/vendi-la-tua-auto/permuta");
  const [isOpen, setIsOpen] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  // Inizializza lo stato leggendo sessionStorage (client-side safe)
  const [hasAcceptedPrivacy, setHasAcceptedPrivacy] = useState<boolean>(false);

  // Carica il valore da sessionStorage dopo il mount (Next.js SSR-safe)
  useEffect(() => {
    const stored = sessionStorage.getItem(PRIVACY_SESSION_KEY);
    if (stored === "true") {
      setHasAcceptedPrivacy(true);
    }

    const inviteTimer = window.setTimeout(() => setShowInvite(true), 2200);
    return () => window.clearTimeout(inviteTimer);
  }, []);

  const dismissInvite = () => {
    setShowInvite(false);
  };

  const openChat = () => {
    dismissInvite();
    setIsOpen(true);
  };

  useEffect(() => {
    const handleExternalOpen = () => {
      setShowInvite(false);
      setIsOpen(true);
    };

    window.addEventListener(OPEN_CHATBOT_EVENT, handleExternalOpen);
    return () => window.removeEventListener(OPEN_CHATBOT_EVENT, handleExternalOpen);
  }, []);

  const handlePrivacyAccept = () => {
    sessionStorage.setItem(PRIVACY_SESSION_KEY, "true");
    setHasAcceptedPrivacy(true);
  };

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      role: "bot",
      text: "Ciao! Come posso aiutarti oggi?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [sessionId] = useState(
    () => "session-" + Math.random().toString(36).substring(7)
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  // Focus automatico sull'input quando la privacy viene accettata
  useEffect(() => {
    if (hasAcceptedPrivacy && isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [hasAcceptedPrivacy, isOpen]);

  // Evita che, soprattutto su mobile, lo scroll della pagina sottostante
  // venga percepito come overflow della finestra della chat.
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const trackQuickReply = (
    reply: (typeof QUICK_REPLIES)[number],
    destination: string
  ) => {
    const analyticsPayload = {
      event: CHATBOT_QUICK_REPLY_EVENT,
      quick_reply_id: reply.id,
      quick_reply_label: reply.label,
      destination,
      page_path: window.location.pathname,
    };
    const analyticsWindow = window as AnalyticsWindow;
    analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
    analyticsWindow.dataLayer.push(analyticsPayload);

    const analyticsBody = JSON.stringify(analyticsPayload);
    let queued = false;

    try {
      queued = navigator.sendBeacon(
        CHATBOT_ANALYTICS_URL,
        new Blob([analyticsBody], { type: "text/plain;charset=UTF-8" })
      );
    } catch {
      // Se il browser rifiuta il beacon, prova comunque con fetch keepalive.
    }

    if (!queued) {
      void fetch(CHATBOT_ANALYTICS_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body: analyticsBody,
        keepalive: true,
      }).catch(() => {
        // Il tracciamento non deve bloccare l'azione dell'utente.
      });
    }
  };

  const handleQuickReply = (reply: (typeof QUICK_REPLIES)[number]) => {
    const destination =
      reply.action === "vehicle_search"
        ? VEHICLE_SEARCH_HASH
        : reply.action === "navigate"
          ? reply.href
          : "local_response:order_status";

    trackQuickReply(reply, destination);

    if (reply.action === "local_response") {
      const messageId = Date.now();
      setMessages((prev) => [
        ...prev,
        {
          id: `${messageId}-user`,
          role: "user",
          text: reply.label,
        },
        {
          id: `${messageId}-bot`,
          role: "bot",
          text: reply.response,
        },
      ]);
      return;
    }

    if (reply.action === "vehicle_search") {
      setIsOpen(false);

      if (window.location.pathname !== "/") {
        window.location.assign("/" + VEHICLE_SEARCH_HASH);
        return;
      }

      window.history.replaceState(null, "", VEHICLE_SEARCH_HASH);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const searchSection = Array.from(
            document.querySelectorAll<HTMLElement>("[data-home-vehicle-search]")
          ).find((element) => element.getClientRects().length > 0);

          searchSection?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      });
      return;
    }

    window.location.assign(reply.href);
  };

  const sendMessage = async (userText: string) => {
    if (!userText || isLoading || !hasAcceptedPrivacy) return;
    setInput("");

    const newMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      text: userText,
    };

    setMessages((prev) => [...prev, newMessage]);
    setIsLoading(true);

    try {
      const response = await fetch(
        CHATBOT_WEBHOOK_URL,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "sendMessage",
            chatInput: userText,
            sessionId: sessionId,
          }),
        }
      );

      let botText =
        "Si è verificato un errore durante la comunicazione con l'assistente.";

      if (response.ok) {
        const data = await response.json();
        if (typeof data === "string") {
          botText = data;
        } else if (data.output) {
          botText = data.output;
        } else if (data.text) {
          botText = data.text;
        } else if (data.message) {
          botText = data.message;
        } else if (data.response) {
          botText = data.response;
        } else if (Array.isArray(data) && data.length > 0 && data[0].output) {
          botText = data[0].output;
        } else {
          botText = JSON.stringify(data);
        }
      }

      setMessages((prev) => [
        ...prev,
        { id: Date.now().toString(), role: "bot", text: botText },
      ]);
    } catch (error) {
      console.error("Error communicating with AI:", error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          role: "bot",
          text: "Si è verificato un errore di rete. Riprova più tardi.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendMessage(input.trim());
  };

  const handlePermutaPrompt = async (
    prompt: (typeof PERMUTA_PROMPTS)[number]
  ) => {
    setInput(prompt.message);
    await sendMessage(prompt.message);
  };

  return (
    <>
      {/* Mobile: fumetto collegato visivamente al pulsante della chat */}
      <aside
        className={cn(
          "fixed bottom-[94px] right-4 z-50 w-[min(300px,calc(100vw-2rem))] transition-all duration-300 lg:hidden",
          isPermutaArea && "hidden",
          showInvite && !isOpen
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-3 opacity-0"
        )}
        aria-hidden={isPermutaArea || !showInvite || isOpen}
        aria-label="Messaggio dell'assistente virtuale"
      >
        <div className="relative rounded-2xl rounded-br-md border border-primary/10 bg-white p-4 pr-10 shadow-[0_18px_50px_rgba(0,13,125,0.24)]">
          <span
            className="absolute -bottom-2 right-5 h-5 w-5 rotate-45 border-b border-r border-primary/10 bg-white"
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={dismissInvite}
            className="absolute right-2.5 top-2.5 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Chiudi messaggio della chat"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-3">
            <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-custom text-white shadow-md">
              <Bot className="h-5 w-5" />
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-400" />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-primary">
                Partiamo dalle tue esigenze
              </p>
              <p className="mt-1.5 text-sm font-medium leading-snug text-slate-800">
                Contattaci in chat e scopri la soluzione giusta per te.
              </p>
              <button
                type="button"
                onClick={openChat}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-custom transition-colors hover:text-primary"
              >
                Avvia la chat
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </aside>
      {/* ── Floating Button ─────────────────────────────────────────────────── */}
      <div
        className={cn(
          "fixed bottom-4 right-4 z-50 transition-all duration-300 transform",
          isOpen ? "scale-0 opacity-0" : "scale-100 opacity-100"
        )}
      >
        <button
          onClick={openChat}
          className="bg-gradient-custom text-white p-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
          aria-label="Apri chat assistente"
        >
          <MessageCircle className="w-8 h-8" />
        </button>
      </div>

      {/* ── Chat Window ─────────────────────────────────────────────────────── */}
      <div
        className={cn(
          "fixed bottom-4 right-4 z-50 w-[350px] max-w-[calc(100dvw-2rem)] h-[500px] max-h-[calc(100dvh-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden overscroll-contain transition-all duration-300 transform origin-bottom-right",
          isOpen
            ? "scale-100 opacity-100"
            : "scale-0 opacity-0 pointer-events-none"
        )}
      >
        {/* Header */}
        <div className="bg-gradient-custom text-white p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-full">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-lg leading-tight">
                Assistente AI
              </h3>
              <p className="text-xs text-white/80">Risponde subito</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50 flex flex-col gap-4 min-h-0">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                "flex max-w-[85%]",
                msg.role === "user"
                  ? "ml-auto justify-end"
                  : "mr-auto justify-start"
              )}
            >
              <div
                className={cn(
                  "p-3 rounded-2xl text-sm shadow-sm",
                  msg.role === "user"
                    ? "bg-primary text-white rounded-br-sm"
                    : "bg-white text-slate-800 border border-slate-100 rounded-bl-sm"
                )}
              >
                {formatMessageText(msg.text, msg.role === "user")}
              </div>
            </div>
          ))}
          {hasAcceptedPrivacy && messages.length === 1 && !isLoading && (
            <div
              className={cn("grid w-full", isPermutaArea ? "gap-1.5" : "gap-2.5")}
              aria-label={isPermutaArea ? "Domande frequenti sulla permuta" : "Risposte rapide"}
            >
              {isPermutaArea
                ? PERMUTA_PROMPTS.map((prompt) => (
                    <button
                      key={prompt.id}
                      type="button"
                      data-permuta-prompt-id={prompt.id}
                      onClick={() => handlePermutaPrompt(prompt)}
                      className="group flex w-full items-center justify-between gap-2 rounded-lg border border-primary/15 bg-white px-3 py-2 text-left text-[12px] font-medium leading-snug text-primary transition-colors duration-200 hover:border-primary/35 hover:bg-primary/[0.03] disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isLoading}
                    >
                      <span>{prompt.label}</span>
                      <ArrowRight className="h-3 w-3 flex-shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </button>
                  ))
                : QUICK_REPLIES.map((reply) => (
                    <button
                      key={reply.id}
                      type="button"
                      data-quick-reply-id={reply.id}
                      onClick={() => handleQuickReply(reply)}
                      className="group flex min-h-12 w-full items-center justify-between gap-3 rounded-xl border border-primary/15 bg-white px-4 py-3 text-left text-[13px] font-semibold leading-snug text-primary shadow-[0_4px_14px_rgba(0,13,125,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/35 hover:bg-primary/[0.03] hover:shadow-[0_8px_22px_rgba(0,13,125,0.12)] active:translate-y-0"
                    >
                      <span>{reply.label}</span>
                      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/5 transition-colors duration-200 group-hover:bg-primary group-hover:text-white">
                        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                    </button>
                  ))}
            </div>
          )}
          {isLoading && (
            <div className="flex max-w-[85%] mr-auto justify-start">
              <div className="p-4 rounded-2xl bg-white border border-slate-100 rounded-bl-sm shadow-sm flex gap-1 items-center">
                <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.3s]" />
                <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce [animation-delay:-0.15s]" />
                <div className="w-2 h-2 bg-primary/40 rounded-full animate-bounce" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* ── Bottom area: Privacy Gate o Input ───────────────────────────────── */}
        {!hasAcceptedPrivacy ? (
          /* Privacy Consent Banner */
          <PrivacyConsent onAccept={handlePrivacyAccept} />
        ) : (
          /* Input form — entra con fade-in */
          <div
            className="p-4 bg-white border-t border-slate-100 flex-shrink-0 animate-in fade-in duration-300"
          >
            <form
              onSubmit={handleSubmit}
              className="flex items-center gap-2 bg-slate-50 rounded-full border border-slate-200 p-1 pr-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi un messaggio..."
                className="min-w-0 flex-1 bg-transparent px-3 py-2 text-base sm:text-sm outline-none text-slate-800 placeholder:text-slate-400"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="bg-primary text-white p-2 rounded-full hover:bg-custom transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        )}
      </div>
    </>
  );
}
